import { type TranscriptSegmentType } from '~/components/AssetModal/shared/components/TranscriptPanel/types';
import { type TimeRange } from '~/swr-hooks/clips/transcript/types';

type GetTranscriptWordsFromSearchResultsParams = {
  segment: TranscriptSegmentType;
  searchPhrase: string;
  enrichedText: string;
};

const resultRegex = /<em>(.*?)<\/em>/;

/**
 * This function extracts time ranges from search results.
 * As search results, backend returns full segment text with searched phrases wrapped with <em> tags.
 * Frontend has to extract found words and match them with already fetched transcript words to get time ranges to highlight words.
 * @see https://www.notion.so/airinc/Asset-Version-Transcript-Paging-Search-3d12e91561d944dab97e3b0eb9d268c4?pvs=4#e5574f5d80644bff89a3c08f2ff08ec8
 */
export const getTranscriptWordsFromSearchResults = ({
  searchPhrase,
  enrichedText,
  segment,
}: GetTranscriptWordsFromSearchResultsParams): TimeRange[] => {
  const wordsRanges: TimeRange[] = [];

  const textWords = enrichedText.split(' ');
  let textWordIndex = 0;
  const phraseWordsCount = searchPhrase.split(' ').length;

  for (let i = 0; i < segment.items.length; i++) {
    const segmentWord = segment.items[i];
    if (segmentWord.type === 'punctuation') {
      continue;
    }
    const wordToCheck = textWords[textWordIndex];

    if (resultRegex.test(wordToCheck)) {
      // if we found matching word, we have to find all words in phrase
      const timeRange: TimeRange = {
        startTimeMs: segmentWord.startTimeMs,
        endTimeMs: segmentWord.endTimeMs,
      };
      let foundWordsCount = 1;
      textWordIndex += 1;

      // we have to check words count in a phrase to know if this is still the old result or new one
      while (resultRegex.test(textWords[textWordIndex]) && foundWordsCount < phraseWordsCount) {
        textWordIndex += 1;
        i += 1;
        if (segment.items[i].type === 'punctuation') {
          i += 1;
        }
        foundWordsCount += 1;
      }
      timeRange.endTimeMs = segment.items[i].endTimeMs;
      wordsRanges.push(timeRange);
    } else {
      textWordIndex += 1;
    }
  }

  return wordsRanges;
};
